import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, Search, Tag, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, photoSrc } from "@/lib/api";
import { QUOTE_URL } from "@/lib/constants";

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("all");
  const [query, setQuery] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api
      .get("/albums")
      .then((r) => setAlbums(r.data))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, []);

  const allPhotos = useMemo(
    () =>
      albums.flatMap((a) =>
        a.photos.map((p) => ({ ...p, albumName: a.name }))
      ),
    [albums]
  );

  useEffect(() => {
    const handler = (e) => {
      const p = allPhotos.find((x) => x.id === e.detail.photoId);
      if (!p) return;
      setLightbox(p);
      document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("open-gallery-photo", handler);
    return () => window.removeEventListener("open-gallery-photo", handler);
  }, [allPhotos]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (q) {
      return allPhotos.filter(
        (p) =>
          p.tags.some((t) => t.includes(q)) ||
          (p.caption || "").toLowerCase().includes(q) ||
          p.albumName.toLowerCase().includes(q)
      );
    }
    if (selected === "all") return allPhotos;
    const album = albums.find((a) => a.id === selected);
    return album ? album.photos.map((p) => ({ ...p, albumName: album.name })) : [];
  }, [q, selected, albums, allPhotos]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative mx-auto max-w-6xl px-4 py-24 sm:px-8 sm:py-32">
      <div className="mb-12 max-w-2xl">
        <p className="font-script text-2xl text-gold">Fresh from the kitchen</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-wine">
          The Gallery
        </h2>
        <p className="mt-4 text-sm sm:text-base text-plum/70">
          Wander through recent creations, or search by theme, colour, style or occasion —
          try <em className="text-burgundy">“gold”</em>, <em className="text-burgundy">“floral”</em> or <em className="text-burgundy">“wedding”</em>.
        </p>
      </div>

      <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="album-filters">
          <FilterPill active={selected === "all" && !q} onClick={() => { setSelected("all"); setQuery(""); }} testId="filter-all">
            All Creations
          </FilterPill>
          {albums.map((a) => (
            <FilterPill
              key={a.id}
              active={selected === a.id && !q}
              onClick={() => { setSelected(a.id); setQuery(""); }}
              testId={`filter-album-${a.id}`}
            >
              {a.name}
              <span className="ml-1.5 text-xs opacity-60">{a.photos.length}</span>
            </FilterPill>
          ))}
        </div>

        <div className="relative w-full max-w-sm">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search theme, colour, occasion…"
            data-testid="gallery-search-input"
            className="w-full rounded-full border border-gold/40 bg-white/85 py-3 pl-11 pr-10 text-sm text-plum placeholder:text-plum/40 shadow-sm outline-none backdrop-blur transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              data-testid="gallery-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-plum/50 hover:text-burgundy"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {q && (
        <p className="mb-6 text-sm text-wine/70" data-testid="search-results-count">
          {visible.length} {visible.length === 1 ? "creation" : "creations"} matching “{query}” across all albums
        </p>
      )}

      {loading ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="mb-6 h-72 animate-pulse rounded-2xl bg-rose/80 break-inside-avoid" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-gold/50 bg-white/60 px-8 py-20 text-center" data-testid="gallery-empty-state">
          <Camera className="mx-auto mb-4 h-8 w-8 text-gold" />
          <p className="font-serif text-2xl text-burgundy">No creations found</p>
          <p className="mt-2 text-sm text-plum/60">
            {q ? "Try another keyword — a colour, theme or occasion." : "New bakes are on their way — check back soon!"}
          </p>
        </div>
      ) : (
        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="gallery-grid">
          <AnimatePresence>
            {visible.map((p) => (
              <motion.figure
                layout
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="group relative mb-6 break-inside-avoid cursor-pointer overflow-hidden rounded-2xl border border-gold/40 bg-white shadow-[0_10px_36px_-14px_rgba(123,45,78,0.3)] gold-glow"
                onClick={() => setLightbox(p)}
                data-testid={`gallery-photo-${p.id}`}
              >
                <img
                  src={photoSrc(p.url)}
                  alt={p.caption || `${p.albumName} creation`}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-plum/85 via-plum/40 to-transparent p-4 pt-12 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-serif text-lg italic text-white">{p.caption || p.albumName}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {p.tags.slice(0, 4).map((t) => (
                      <span key={t} className="rounded-full border border-gold/60 bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gold">
                        {t}
                      </span>
                    ))}
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl border-gold/40 bg-blush p-2 sm:p-4" data-testid="photo-lightbox">
          {lightbox && (
            <div>
              <img
                src={photoSrc(lightbox.url)}
                alt={lightbox.caption || "Cake creation"}
                className="max-h-[65vh] w-full rounded-xl object-contain bg-white"
              />
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-serif text-xl text-burgundy">{lightbox.caption || lightbox.albumName}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-gold" />
                    {lightbox.tags.map((t) => (
                      <span key={t} className="rounded-full border border-gold/50 bg-white px-2.5 py-0.5 text-xs text-wine">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <a
                  href={QUOTE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lightbox-quote-button"
                  className="shrink-0 rounded-full border border-gold bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep"
                >
                  Request a Quote
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-4 py-2 text-xs sm:text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 ${
        active
          ? "border-gold bg-burgundy text-white shadow-[0_10px_24px_-8px_rgba(123,45,78,0.6)]"
          : "border-burgundy/25 bg-white/75 text-wine hover:border-gold hover:text-golddeep"
      }`}
    >
      {children}
    </button>
  );
}
